import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mic, MicOff, Video, VideoOff, Star, Youtube, Sparkles, Clock, Play } from "lucide-react";
import { formatNumber, timeAgo, DELIVERY_SCOPE_LABEL } from "../lib/constants";
import { extractYouTubeId, youtubeThumb, youtubeEmbed } from "../lib/youtube";
import VerifiedBadge from "./VerifiedBadge";

export const CreatorCard = ({ c }) => {
  return (
    <Link
      to={`/creators/${c.id}`}
      data-testid={`creator-card-${c.id}`}
      className="group block cc-surface cc-card-hover rounded-2xl p-6 cc-anim-up relative"
    >
      {c.featured && (
        <span
          data-testid={`featured-badge-${c.id}`}
          className="cc-featured-badge absolute top-4 right-4 inline-flex items-center gap-1"
        >
          <Sparkles size={10} /> Featured
        </span>
      )}

      <div className="flex items-start gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-xl overflow-hidden border border-[#15152a] bg-[#06060c]">
            {c.avatar_url ? (
              <img src={c.avatar_url} alt={c.username} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl text-[#00ff88]">{c.glyph || "◈"}</div>
            )}
          </div>
          <span
            title={c.status}
            className={`cc-status-dot ${c.status} absolute -bottom-0.5 -right-0.5 ring-2 ring-[#0a0a14]`}
          />
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[#00ff88] text-sm">{c.glyph}</span>
            <h3 className="text-white font-semibold truncate">{c.username}</h3>
            <VerifiedBadge verified={c.verified} testIdSuffix={c.id} size="xs" />
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className="flex items-center gap-1 text-[11px] text-[#a0a0b0] font-mono">
              <Youtube size={11} />
              {formatNumber(c.subscribers)} subs
            </span>
            <span className="flex items-center gap-1 text-[11px] text-[#a0a0b0] font-mono">
              <Star size={11} className="fill-[#ffaa00] text-[#ffaa00]" />
              {c.rating} · {c.reviews_count}
            </span>
          </div>
        </div>
      </div>

      {/* Prominent price block */}
      <div className="mt-5 flex items-end justify-between">
        <div>
          <div className="cc-overline">Starting at</div>
          <div
            className="font-mono text-3xl leading-none text-[#00ff88] mt-1.5"
            style={{ textShadow: "0 0 18px rgba(0,255,136,0.35)" }}
          >
            ${c.price}
          </div>
        </div>
        <div className="text-right">
          <div className="cc-overline">Quality</div>
          <div className="font-mono text-xs text-white mt-1.5">{c.quality}</div>
        </div>
      </div>

      {/* Games */}
      <div className="flex flex-wrap gap-1.5 mt-5">
        {c.games.slice(0, 5).map((g) => (
          <span key={g} className="cc-chip">{g}</span>
        ))}
        {c.games.length > 5 && <span className="cc-chip">+{c.games.length - 5}</span>}
      </div>

      {/* Tech row */}
      <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] text-[#a0a0b0]">
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded border ${c.has_mic ? "border-[#00ff88]/30 text-[#00ff88] bg-[#00ff88]/5" : "border-[#15152a] text-[#606070]"}`}>
          {c.has_mic ? <Mic size={11} /> : <MicOff size={11} />} mic
        </span>
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded border ${c.has_facecam ? "border-[#00ff88]/30 text-[#00ff88] bg-[#00ff88]/5" : "border-[#15152a] text-[#606070]"}`}>
          {c.has_facecam ? <Video size={11} /> : <VideoOff size={11} />} cam
        </span>
        <span className="px-2 py-0.5 rounded border border-[#15152a] bg-[#06060c]">{c.languages.join(" · ")}</span>
        <span className="flex items-center gap-1 px-2 py-0.5 rounded border border-[#15152a] bg-[#06060c]">
          <Clock size={11} className="text-[#00ff88]" /> <span className="font-mono">{c.avg_delivery_days ?? 7}d</span>
        </span>
        {c.delivery_scope === "full" && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded border border-[#00ff88]/30 text-[#00ff88] bg-[#00ff88]/5">
            <Youtube size={11} /> upload
          </span>
        )}
      </div>

      {/* Services */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {c.services.map((s) => (
          <span key={s} className="text-[10px] uppercase tracking-widest text-[#00ff88]/80 border border-[#00ff88]/20 rounded px-1.5 py-0.5">{s}</span>
        ))}
      </div>

      {/* Latest video — inline playable */}
      <div className="mt-5 pt-5 border-t border-[#15152a]">
        <div className="cc-overline mb-2">Latest Video</div>
        <LatestVideo creator={c} />
      </div>
    </Link>
  );
};

const LatestVideo = ({ creator }) => {
  const [playing, setPlaying] = useState(false);
  const first = (creator.portfolio || [])[0];
  const ytId = first ? extractYouTubeId(first.url) : null;
  const title = (first?.title && !/^Portfolio link #\d+$/.test(first.title))
    ? first.title
    : creator.last_video_title || (ytId ? `youtu.be/${ytId}` : "");
  const when = first?.at || creator.last_video_at;

  if (!first) {
    return <div className="text-sm text-[#606070]">No videos yet.</div>;
  }

  if (!ytId) {
    return (
      <a
        href={first.url}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-sm text-[#00ff88] hover:underline truncate inline-block"
      >
        {first.url}
      </a>
    );
  }

  return (
    <div>
      <div className="relative aspect-video rounded-lg overflow-hidden bg-[#06060c] border border-[#15152a] group">
        {playing ? (
          <iframe
            data-testid={`latest-iframe-${creator.id}`}
            src={youtubeEmbed(ytId)}
            title={title || "Latest video"}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <>
            <img
              src={youtubeThumb(ytId, "hqdefault")}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                if (!e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = "1";
                  e.currentTarget.src = youtubeThumb(ytId, "mqdefault");
                }
              }}
            />
            <button
              type="button"
              data-testid={`latest-play-${creator.id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPlaying(true); }}
              className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/40 transition-colors"
              aria-label="Play latest video"
            >
              <span className="w-12 h-12 rounded-full bg-[#00ff88]/95 text-[#06060c] flex items-center justify-center shadow-[0_6px_24px_-4px_rgba(0,255,136,0.5)] group-hover:scale-105 transition-transform">
                <Play size={18} className="ml-0.5" fill="currentColor" />
              </span>
            </button>
          </>
        )}
      </div>
      {title && <div className="text-sm text-white truncate mt-2">{title}</div>}
      <div className="text-[11px] text-[#606070] mt-0.5">{timeAgo(when)}</div>
    </div>
  );
};

export default CreatorCard;
